import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';

// 3rd party imports
import { DxPopupComponent } from 'devextreme-angular';

// cc-imports
import { IconConstants, Utils } from 'cc-common';

// Component imports
import { VendorAccountEditorComponent } from '../vendor-account-editor/vendor-account-editor.component';
import { VendorAccountHistoryComponent } from '../vendor-account-history/vendor-account-history.component';
import { VendorAccountNotesComponent } from '../vendor-account-notes/vendor-account-notes.component';

// service imports
import { VendorAccountEditService } from '../../_services/vendor-account-edit.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { UserService } from '../../../_services/data-services/user.service';
import { HelperService } from '../../../_services/shared-services/helper.service';

// Model imports
import { VendorAccountResultDto } from '../../_models/vendor-account-result-dto';
import { ManageVendorAccountsResultDto } from '../../_models/manage-vendor-accounts-result-dto';
import { WFEVendorParametersDto } from '../../../workflow/_models/common/wfe-vendor-parameter-dto';
import { ManageVendorAccountsParameterDto } from '../../_models/manage-vendor-accounts-parameter-dto';

@Component({
    selector: 'app-manage-vendor-accounts',
    templateUrl: './manage-vendor-accounts.component.html',
    styleUrls: ['./manage-vendor-accounts.component.scss']
})
export class ManageVendorAccountsComponent implements OnInit {
    @ViewChild('historyPopup', { static: false }) historyPopup: DxPopupComponent;
    @ViewChild('manageVendorAccountPopup', { static: false }) manageVendorAccountPopup: DxPopupComponent;
    @ViewChild('vendorAccountConfig', { static: false }) vendorAccountConfig: VendorAccountEditorComponent;
    @ViewChild('vendorAccountHistory', { static: false }) vendorAccountHistory: VendorAccountHistoryComponent;
    @ViewChild('vendorAccountNotes', { static: false }) vendorAccountNotes: VendorAccountNotesComponent;

    @Input() workflowId: number;
    @Input() workflowStatus: string;
    @Input() vendorCode: string;
    @Input() vendorName: string;
    @Input() associationVendorId: number;
    @Input() communityId: number;
    @Input() communityName: string;
    @Input() visible = false;

    @Output() closeManageVendorAccounts = new EventEmitter<boolean>();
    @Output() onHidden = new EventEmitter<boolean>();
    @Output() refreshManageVendorAccounts = new EventEmitter<any>();
    @Output() onVendorAccountEditorClick = new EventEmitter<VendorAccountResultDto>();

    manageVendorAccountsSource: ManageVendorAccountsResultDto;
    hasSourceData = false;
    showHistoryPopUp = false;
    showAddEditVendorAccount = false;
    showNotesPopUp = false;

    addEditPopUpTitle: string;
    vendorAccountConfigMode: string;

    isInternalUser = false;

    selectedVendorAccount: VendorAccountResultDto;

    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconHistory: string = IconConstants.HISTORY;
    iconClose: string = IconConstants.CLOSE;
    iconCheck: string = IconConstants.CHECK;

    constructor(
        private readonly vendorAccountEditorService: VendorAccountEditService,
        private readonly errorService: ServerErrorService,
        private readonly userService: UserService,
        private readonly helperService: HelperService
    ) {}

    ngOnInit() {
        this.isInternalUser = this.userService.isInternal();

        this.show();
    }

    show(): void {
        this.visible = true;
        this.hasSourceData = false;
        this.resetVendorAccounts();
        if (!!this.workflowId && !!this.workflowStatus) {
            this.getVendorAccountEditDetail();
        } else {
            this.getVendorAccounts();
        }
    }

    onVendorAccountShown(): void {
        this.visible = true;
        this.manageVendorAccountPopup.instance.repaint();
    }

    onVendorAccountHide(): void {
        this.visible = false;
        this.closeManageVendorAccounts.emit(true);
    }

    onActionClick(record): void {
        this.selectedVendorAccount = record;
        this.onVendorAccountEditorClick.emit(this.selectedVendorAccount);
    }

    onLogClick(record): void {
        this.selectedVendorAccount = record;
        this.showHistoryPopUp = true;
    }

    onAccountEditNotesClick(record): void {
        this.selectedVendorAccount = record;
        this.showNotesPopUp = true;
    }

    onVendorAccountHistoryClose(): void {
        this.showHistoryPopUp = false;
    }

    onVendorAccountNotesClose(event): void {
        if (event) {
            this.selectedVendorAccount.hasNotes = event;
        }
        this.showNotesPopUp = false;
        this.reloadManageAccounts();
    }

    onAddNewVendorAccount(): void {
        this.selectedVendorAccount = null;
        this.onVendorAccountEditorClick.emit(this.selectedVendorAccount);
    }

    onVendorNameClick(): void {
        if (Utils.notNullAndDefined(this.vendorName)) {
            const contextType = 'Community';
            const contextId = this.communityId;
            const vendorCode = this.vendorCode;
            const queryString = `contextType=${encodeURIComponent(
                contextType
            )}&contextId=${contextId}&vendorCode=${encodeURIComponent(vendorCode)}`;
            this.helperService.openDefaultVendorManagementPageInNewWindow(queryString);
        }
    }

    private getVendorAccountEditDetail(clearCache = false): void {
        const baseParams: WFEVendorParametersDto = new WFEVendorParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.communityId;
        baseParams.workflowStatus = this.workflowStatus;
        baseParams.vendorCode = this.vendorCode;
        baseParams.clearCache = clearCache;

        this.vendorAccountEditorService.getVendorAccountDetail(baseParams).subscribe(
            (result: ManageVendorAccountsResultDto) => {
                this.prepareMangeVendorAccountSource(result);
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getVendorAccounts(): void {
        const baseParam: ManageVendorAccountsParameterDto = new ManageVendorAccountsParameterDto();
        baseParam.communityId = this.communityId;
        baseParam.payeeCode = this.vendorCode;

        this.vendorAccountEditorService.getVendorAccount(baseParam).subscribe(
            (result: ManageVendorAccountsResultDto) => {
                this.prepareMangeVendorAccountSource(result);
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private prepareMangeVendorAccountSource(result: ManageVendorAccountsResultDto): void {
        this.manageVendorAccountsSource = result;
        this.hasSourceData =
            this.manageVendorAccountsSource.vendorAccounts && this.manageVendorAccountsSource.vendorAccounts.length > 0;
        setTimeout(() => {
            this.manageVendorAccountPopup.instance.repaint();
        }, 100);
    }

    private resetVendorAccounts(): void {
        this.manageVendorAccountsSource = new ManageVendorAccountsResultDto();
        this.manageVendorAccountsSource.vendorAccounts = [];
    }

    public reloadManageAccounts(): void {
        this.resetVendorAccounts();
        setTimeout(() => {
            if (!!this.workflowId && !!this.workflowStatus) {
                this.getVendorAccountEditDetail(true);
            } else {
                this.getVendorAccounts();
            }
        }, 200);
    }

    public refreshVendorAccountHistory(): void {
        if (this.vendorAccountHistory) {
            this.vendorAccountHistory.refreshVendorAccountHistory();
        }
    }

    public refreshVendorAccountNotes(): void {
        if (this.vendorAccountNotes) {
            this.vendorAccountNotes.refreshVendorAccountNotes();
        }
    }
}
